/** 학원 정보 설정 — 좌: 수정 폼 / 우: 학부모 앱(모바일) 미리보기 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { IoPhonePortraitOutline, IoOpenOutline, IoRefresh } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useAcademy } from '../../context/AcademyContext';
import { useConsoleAcademy } from '../../components/console/ConsoleLayout';
import { PageHead, PageTitle, PrimaryBtn, Card } from './consoleUi';
import AcademyForm, { emptyForm, formFromAcademy, buildAcademyData, missingRequired } from './AcademyForm';
import DaumPostcodeModal from '../../components/common/DaumPostcodeModal';

const ConsoleSettingsPage = () => {
  const { academy } = useConsoleAcademy();
  const { reloadAcademies } = useAcademy();
  const aid = academy.id;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [previewKey, setPreviewKey] = useState(0);
  const [postcodeOpen, setPostcodeOpen] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const load = useCallback(async () => {
    setLoading(true);
    const fresh = await academyService.getAcademy(aid);
    setForm(formFromAcademy(fresh || {}));
    setLoading(false);
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const onAddPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const url = await academyService.uploadAcademyPromoPhoto(aid, file);
        uploaded.push({ url, label: '' });
      }
      set('photos', [...form.photos, ...uploaded]);
    } catch (err) {
      alert('사진 업로드에 실패했어요.');
    } finally {
      setUploading(false);
    }
  };
  const removePhoto = (i) => set('photos', form.photos.filter((_, idx) => idx !== i));
  const setPhotoLabel = (i, v) => set('photos', form.photos.map((p, idx) => (idx === i ? { ...p, label: v } : p)));
  const addTuition = () => set('tuition', [...form.tuition, { label: '', price: '' }]);
  const setTuition = (i, k, v) => set('tuition', form.tuition.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const delTuition = (i) => set('tuition', form.tuition.filter((_, idx) => idx !== i));
  const openPostcode = () => setPostcodeOpen(true);

  const missing = missingRequired(form);
  const canSave = missing.length === 0;

  const onSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await academyService.updateAcademy(aid, buildAcademyData(form));
      await reloadAcademies();
      setPreviewKey((k) => k + 1);   // 미리보기 새로고침
      setSaved(true);
    } catch (err) {
      console.error('저장 실패:', err);
      alert('저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const formProps = {
    form, set, uploading, photoInputId: 'settings-photos',
    onAddPhotos, removePhoto, setPhotoLabel,
    addTuition, setTuition, delTuition, openPostcode,
  };

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>학원 정보</h2>
          <p>학부모에게 보이는 내 학원 정보를 수정해요. 오른쪽에서 실제 모바일 화면을 확인할 수 있어요.</p>
        </PageTitle>
        <SaveBar>
          <Hint $ok={canSave}>
            {saved ? '저장됨 ✓' : canSave ? '수정 후 저장을 눌러주세요' : `필수 ${missing.length}개 남음`}
          </Hint>
          <PrimaryBtn onClick={onSave} disabled={!canSave || saving}>{saving ? '저장 중…' : '저장'}</PrimaryBtn>
        </SaveBar>
      </PageHead>

      <Cols>
        <Card>
          {loading ? <Loading>불러오는 중…</Loading> : <AcademyForm {...formProps} />}
        </Card>

        <PreviewCol>
          <PreviewHead>
            <span><IoPhonePortraitOutline size={16} /> 학부모 앱 미리보기</span>
            <PreviewBtns>
              <IconLink type="button" onClick={() => setPreviewKey((k) => k + 1)} title="새로고침"><IoRefresh size={16} /></IconLink>
              <IconLink as="a" href="/app/home" target="_blank" rel="noreferrer" title="새 탭에서 열기"><IoOpenOutline size={16} /></IconLink>
            </PreviewBtns>
          </PreviewHead>
          <Phone>
            <PhoneScreen
              key={previewKey}
              title="학부모 앱 미리보기"
              src="/app/home"
            />
          </Phone>
          <PreviewNote>학부모가 앱에서 보는 화면이에요. 저장하면 반영돼요.</PreviewNote>
        </PreviewCol>
      </Cols>

      {postcodeOpen && (
        <DaumPostcodeModal
          onClose={() => setPostcodeOpen(false)}
          onComplete={(addr) => set('location', addr)}
        />
      )}
    </>
  );
};

export default ConsoleSettingsPage;

const SaveBar = styled.div` display: flex; align-items: center; gap: 14px; `;
const Hint = styled.span` font-size: 13px; font-weight: 600; color: ${({ $ok }) => ($ok ? '#16A34A' : '#9CA3AF')}; `;
const Loading = styled.div` padding: 60px; text-align: center; color: #9CA3AF; `;

const Cols = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) 420px; gap: 22px; align-items: start;
  @media (max-width: 1180px) { grid-template-columns: 1fr; }
`;

const PreviewCol = styled.div` position: sticky; top: 28px; `;
const PreviewHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
  span { display: flex; align-items: center; gap: 7px; font-size: 14px; font-weight: 800; color: #1F2937; }
`;
const PreviewBtns = styled.div` display: flex; gap: 6px; `;
const IconLink = styled.button`
  display: inline-flex; padding: 7px; border-radius: 8px; color: #6B7280;
  border: 1px solid #EAEDF3; background: #fff;
  &:hover { background: #F3F4F6; color: #374151; }
`;
const Phone = styled.div`
  width: 390px; max-width: 100%; height: 760px; margin: 0 auto;
  border: 10px solid #1A1A2E; border-radius: 38px; overflow: hidden; background: #fff;
  box-shadow: 0 16px 40px rgba(0,0,0,0.18);
`;
const PhoneScreen = styled.iframe` width: 100%; height: 100%; border: none; `;
const PreviewNote = styled.p` text-align: center; font-size: 12px; color: #9CA3AF; margin-top: 12px; `;
